/**
 * AWS Amplify configuration and Axios interceptors.
 *
 * Amplify is configured from VITE_* environment variables so that the same
 * build artefact can be deployed to different environments by changing the
 * runtime config (injected at deploy time via CloudFront / S3 config object
 * or environment-specific .env files during local development).
 *
 * Required environment variables:
 *   VITE_COGNITO_USER_POOL_ID      — Cognito User Pool ID (e.g. us-east-1_XXXXXXXXX)
 *   VITE_COGNITO_CLIENT_ID         — Cognito App Client ID
 *   VITE_COGNITO_DOMAIN            — Cognito Hosted UI domain (e.g. auth.example.com)
 *   VITE_COGNITO_REDIRECT_SIGN_IN  — OAuth redirect sign-in URL
 *   VITE_COGNITO_REDIRECT_SIGN_OUT — OAuth redirect sign-out URL
 *   VITE_API_URL                   — Base URL for the API Gateway (e.g. https://api.example.com)
 */

import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from '@aws-amplify/auth';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// ---------------------------------------------------------------------------
// OAuth data models (see design.md: Data Models)
// ---------------------------------------------------------------------------

/**
 * OAuth `prompt` values we use against the Hosted UI / Entra.
 * - 'select_account': always show the account picker (primary fix).
 * - 'login': force full re-authentication (escalation if select_account proves
 *   insufficient with the Entra tenant config).
 * - 'none': never used here; kept for completeness of the OIDC enum.
 */
export type OAuthPrompt = 'select_account' | 'login' | 'none';

/** Runtime OAuth config read from VITE_* env vars. */
export interface CognitoOAuthConfig {
  /** VITE_COGNITO_DOMAIN (Hosted UI domain, no scheme). */
  domain: string;
  /** VITE_COGNITO_CLIENT_ID. */
  clientId: string;
  /** this-origin /callback URL (must be registered). */
  redirectSignIn: string;
  /** this-origin root URL (must be a registered logout URL). */
  redirectSignOut: string;
  identityProvider: 'MicrosoftEntraID';
  /** ['openid','email','profile'] */
  scopes: readonly string[];
}

/** Parameters for constructing the /oauth2/authorize request. */
export interface AuthorizeParams {
  config: CognitoOAuthConfig;
  prompt: OAuthPrompt;
  /** base64url(SHA256(codeVerifier)) */
  codeChallenge: string;
  codeChallengeMethod: 'S256';
  /** CSRF/state token */
  state: string;
}

/**
 * The set of MediaManager origins registered on the Cognito app client. An
 * authorize/logout request is only valid from one of these origins (scheme +
 * host + port), because the derived `redirect_uri`/`logout_uri` must exactly
 * match a registered callback/logout URL or Cognito rejects it with a
 * redirect_mismatch. Mirrors the callback/logout URL lists in
 * infra/lib/constructs/cognito-construct.ts.
 */
export const REGISTERED_ORIGINS = [
  'https://mediamanager.greatrun.org',
  'https://dhadios8999ew.cloudfront.net',
  'http://localhost:3000',
] as const;

// ---------------------------------------------------------------------------
// Amplify configuration
// ---------------------------------------------------------------------------

// Amplify's `redirectSignIn` / `redirectSignOut` accept arrays of URLs. When
// multiple URLs are configured, Amplify automatically selects the one whose
// origin matches `window.location.origin` at the time the OAuth flow starts.
// We therefore offer every known origin so the flow completes from whichever
// domain the user started on — the new domain (mediamanager.greatrun.org), the
// old CloudFront domain (dhadios8999ew.cloudfront.net), and localhost for dev.
// The env-var values are kept first so config stays overridable per-environment,
// then merged with the known origins, de-duplicated, and empties filtered out.
// NOTE: every URL here MUST also be registered in the Cognito app client's
// callback/logout URL lists or Cognito rejects it with a redirect_mismatch.
// localhost:5173 is intentionally omitted because it is NOT registered in the
// Cognito app client (only localhost:3000 is).
const redirectSignIn = Array.from(new Set([
  import.meta.env.VITE_COGNITO_REDIRECT_SIGN_IN as string,
  'https://mediamanager.greatrun.org/callback',
  'https://dhadios8999ew.cloudfront.net/callback',
  'http://localhost:3000/callback',
].filter(Boolean)));

const redirectSignOut = Array.from(new Set([
  import.meta.env.VITE_COGNITO_REDIRECT_SIGN_OUT as string,
  'https://mediamanager.greatrun.org',
  'https://dhadios8999ew.cloudfront.net',
  'http://localhost:3000',
].filter(Boolean)));

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID as string,
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID as string,
      loginWith: {
        oauth: {
          domain: import.meta.env.VITE_COGNITO_DOMAIN as string,
          scopes: ['openid', 'email', 'profile'],
          redirectSignIn,
          redirectSignOut,
          responseType: 'code',
        },
      },
    },
  },
}, { ssr: false });

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL as string,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ---------------------------------------------------------------------------
// Request interceptor — inject JWT into every request
// ---------------------------------------------------------------------------

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      // First try reading token from localStorage (our manual OAuth flow)
      const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
      const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
      const lastUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
      if (lastUser) {
        const idToken = localStorage.getItem(`${keyPrefix}.${lastUser}.idToken`);
        if (idToken) {
          config.headers.set('Authorization', idToken);
          return config;
        }
      }

      // Fallback: Amplify session (for email/password sign-in)
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (idToken) {
        config.headers.set('Authorization', idToken);
      }
    } catch {
      // Not authenticated — let the request proceed without a token
    }
    return config;
  },
  (error: unknown) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Response interceptor — handle 429 rate-limit responses
// ---------------------------------------------------------------------------

/**
 * Maximum number of automatic retries on a 429 response.
 * We retry once after the Retry-After delay to give the user a seamless
 * experience for brief rate-limit windows (Requirement 14.3, 14.5).
 */
const MAX_RATE_LIMIT_RETRIES = 1;

// Extend the Axios config type to track retry attempts
interface RetryConfig extends InternalAxiosRequestConfig {
  _rateLimitRetries?: number;
  /** Set once we've attempted a token refresh for a 401 on this request. */
  _authRefreshAttempted?: boolean;
}

/**
 * Clear locally-cached Cognito tokens (our manual OAuth flow) so ProtectedRoute
 * sees an unauthenticated session and the app shows the sign-in screen instead
 * of leaving stale tokens that keep failing.
 *
 * Exported for reuse by the federated sign-out helper (completeFederatedSignOut).
 */
export function clearLocalAuthTokens(): void {
  try {
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
    const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
    const lastUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
    if (lastUser) {
      localStorage.removeItem(`${keyPrefix}.${lastUser}.idToken`);
      localStorage.removeItem(`${keyPrefix}.${lastUser}.accessToken`);
      localStorage.removeItem(`${keyPrefix}.${lastUser}.refreshToken`);
      localStorage.removeItem(`${keyPrefix}.LastAuthUser`);
    }
  } catch {
    // ignore — best-effort cleanup
  }
}

/**
 * Refreshes the session using the refresh token stored by the manual OAuth flow
 * (see CallbackPage). The app exchanges the authorization code itself and keeps
 * the tokens in localStorage because Amplify's fetchAuthSession hangs behind
 * CloudFront in Amplify v6 — so Amplify never holds the session and cannot
 * refresh it. We therefore call Cognito's /oauth2/token endpoint directly with
 * grant_type=refresh_token, persist the new id/access tokens, and return the new
 * ID token. Returns null when there is no stored refresh token or the refresh is
 * rejected (e.g. the 30-day refresh window has lapsed).
 */
async function refreshTokensManually(): Promise<string | null> {
  try {
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
    const domain = import.meta.env.VITE_COGNITO_DOMAIN as string;
    if (!clientId || !domain) {
      return null;
    }
    const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
    const lastUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
    if (!lastUser) {
      return null;
    }
    const refreshToken = localStorage.getItem(`${keyPrefix}.${lastUser}.refreshToken`);
    if (!refreshToken) {
      return null;
    }

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: clientId,
      refresh_token: refreshToken,
    });
    const response = await fetch(`https://${domain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    if (!response.ok) {
      return null;
    }
    const tokens = (await response.json()) as {
      id_token?: string;
      access_token?: string;
      // A refresh-token grant does NOT return a new refresh_token; the existing
      // one remains valid until the end of its 30-day window.
      refresh_token?: string;
    };
    if (!tokens.id_token) {
      return null;
    }
    localStorage.setItem(`${keyPrefix}.${lastUser}.idToken`, tokens.id_token);
    if (tokens.access_token) {
      localStorage.setItem(`${keyPrefix}.${lastUser}.accessToken`, tokens.access_token);
    }
    if (tokens.refresh_token) {
      localStorage.setItem(`${keyPrefix}.${lastUser}.refreshToken`, tokens.refresh_token);
    }
    return tokens.id_token;
  } catch {
    return null;
  }
}

/**
 * When a session genuinely lapses (refresh failed), send the user to sign in
 * again rather than leaving them staring at a "network error". We preserve the
 * current path so they return where they were after re-auth. A hard redirect is
 * used so the whole app (and Redux) reinitialises cleanly against the login flow.
 * Guarded so we only ever redirect once and never from the login/callback pages
 * (which would create a redirect loop).
 */
let redirectingToLogin = false;
/** Test-only: reset the one-shot redirect guard between test cases. */
export function __resetSessionExpiredGuard(): void {
  redirectingToLogin = false;
}
export function handleSessionExpired(): void {
  clearLocalAuthTokens();
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (redirectingToLogin || path.startsWith('/login') || path.startsWith('/callback')) {
    return;
  }
  redirectingToLogin = true;
  const from = encodeURIComponent(path + window.location.search);
  window.location.assign(`/login?from=${from}`);
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetryConfig | undefined;

    // Handle 401 Unauthorized — token might have expired, try refreshing ONCE.
    if (error.response?.status === 401 && config) {
      if (!config._authRefreshAttempted) {
        config._authRefreshAttempted = true;
        try {
          // Primary path: refresh via the refresh token stored by the manual
          // OAuth flow (CallbackPage). Amplify never holds this session because
          // fetchAuthSession hangs behind CloudFront in Amplify v6, so a manual
          // /oauth2/token refresh is what actually keeps users signed in for the
          // full 30-day refresh window. refreshTokensManually persists the new
          // tokens to localStorage and returns the new ID token.
          let newToken = await refreshTokensManually();

          // Fallback: if there is no stored refresh token (e.g. an older
          // email/password Amplify session), try Amplify's own refresh.
          if (!newToken) {
            const session = await fetchAuthSession({ forceRefresh: true });
            newToken = session.tokens?.idToken?.toString() ?? null;
            if (newToken) {
              // Mirror the manual flow's localStorage write so subsequent
              // requests pick up the refreshed token.
              const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
              const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
              const lastUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
              if (lastUser) {
                localStorage.setItem(`${keyPrefix}.${lastUser}.idToken`, newToken);
              }
            }
          }

          if (!newToken) {
            throw new Error('No token after refresh');
          }
          if (config.headers) {
            config.headers.set('Authorization', newToken);
          }
          return apiClient(config);
        } catch {
          // Refresh failed — the session has genuinely lapsed. Send to sign-in
          // cleanly instead of surfacing a raw network/auth error.
          handleSessionExpired();
        }
      }
      // Already attempted a refresh (or it failed) — surface as a clean auth error.
      return Promise.reject(error);
    }

    if (error.response?.status === 429 && config) {
      const retries = config._rateLimitRetries ?? 0;

      if (retries < MAX_RATE_LIMIT_RETRIES) {
        // Read the Retry-After header (seconds) and wait before retrying
        const retryAfterHeader = error.response.headers['retry-after'];
        const retryAfterSeconds = retryAfterHeader ? parseInt(retryAfterHeader, 10) : 1;
        const delayMs = Number.isFinite(retryAfterSeconds) ? retryAfterSeconds * 1000 : 1000;

        await new Promise<void>((resolve) => setTimeout(resolve, delayMs));

        config._rateLimitRetries = retries + 1;
        return apiClient(config);
      }

      // Exhausted retries — surface the 429 to the caller so the UI can
      // display the rate-limit banner (Requirement 14.5).
    }

    return Promise.reject(error);
  },
);

// ---------------------------------------------------------------------------
// Auth helper utilities
// ---------------------------------------------------------------------------

/**
 * Resolve the origin-correct OAuth config for a federated login/logout request.
 *
 * Returns a config whose `redirectSignIn === origin + '/callback'` and
 * `redirectSignOut === origin`, both of which are members of the registered
 * callback/logout URL set on the Cognito app client (see REGISTERED_ORIGINS and
 * infra/lib/constructs/cognito-construct.ts). `domain` and `clientId` are read
 * from the VITE_* env vars.
 *
 * Throws when `origin` is not one of the supported MediaManager origins, or when
 * the required env vars are missing, so callers can surface an error without
 * navigating the browser. Performs no mutation of global state.
 *
 * @param origin Normally `window.location.origin`.
 */
export function getOAuthConfigForOrigin(origin: string): CognitoOAuthConfig {
  if (!REGISTERED_ORIGINS.includes(origin as (typeof REGISTERED_ORIGINS)[number])) {
    throw new Error(
      `Unsupported origin for OAuth: "${origin}". Expected one of: ${REGISTERED_ORIGINS.join(', ')}.`,
    );
  }

  const domain = import.meta.env.VITE_COGNITO_DOMAIN as string;
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
  if (!domain || !clientId) {
    throw new Error(
      'Missing Cognito OAuth configuration: VITE_COGNITO_DOMAIN and VITE_COGNITO_CLIENT_ID must be defined.',
    );
  }

  return {
    domain,
    clientId,
    redirectSignIn: `${origin}/callback`,
    redirectSignOut: origin,
    identityProvider: 'MicrosoftEntraID',
    scopes: ['openid', 'email', 'profile'],
  };
}

/**
 * Construct the Cognito Hosted UI `/oauth2/authorize` URL for a federated login.
 *
 * Pure function with no side effects: given the same `params` it always returns
 * the same string and never reads or mutates global state. The returned URL is
 * `https://{domain}/oauth2/authorize?...` and contains exactly the following
 * query parameters, all URL-encoded:
 *   - `client_id`                — the Cognito app-client id
 *   - `response_type=code`       — authorization-code (PKCE) flow
 *   - `scope`                    — the config's scopes joined by a single space
 *   - `redirect_uri`             — the origin-correct `/callback` URL
 *   - `identity_provider=MicrosoftEntraID`
 *   - `prompt`                   — `select_account` or `login`
 *   - `code_challenge`           — base64url(SHA-256(code_verifier))
 *   - `code_challenge_method=S256`
 *   - `state`                    — CSRF/state token
 *
 * See design.md (Function 3: buildAuthorizeUrl) and Requirements 1.1–1.4, 2.1,
 * 5.1, 5.3.
 */
export function buildAuthorizeUrl(params: AuthorizeParams): string {
  const { config, prompt, codeChallenge, codeChallengeMethod, state } = params;

  // URLSearchParams URL-encodes every value on toString(), keeping this a pure
  // string transform of the input.
  const query = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    scope: config.scopes.join(' '),
    redirect_uri: config.redirectSignIn,
    identity_provider: config.identityProvider,
    prompt,
    code_challenge: codeChallenge,
    code_challenge_method: codeChallengeMethod,
    state,
  });

  return `https://${config.domain}/oauth2/authorize?${query.toString()}`;
}

// ---------------------------------------------------------------------------
// PKCE helpers (RFC 7636)
// ---------------------------------------------------------------------------

/**
 * Storage key under which the PKCE `code_verifier` is persisted at login.
 *
 * The name intentionally contains both `oauthPKCE` and `codeVerifier` so it is
 * discovered by CallbackPage's verifier lookup, which scans storage for keys
 * including `codeVerifier`, `pkce`, or `oauthPKCE`. It is written to
 * sessionStorage, which CallbackPage searches first.
 */
export const PKCE_VERIFIER_STORAGE_KEY = 'oauthPKCE.codeVerifier';

/** Storage key for the OAuth `state` token written alongside the verifier. */
export const OAUTH_STATE_STORAGE_KEY = 'oauthState';

/**
 * Base64url-encode raw bytes (RFC 4648 §5): standard base64 with `+`→`-`,
 * `/`→`_`, and trailing `=` padding removed. Produces URL-safe output suitable
 * for a PKCE `code_verifier`/`code_challenge`.
 */
function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Generate a high-entropy, URL-safe random string of `byteLength` random bytes
 * (base64url-encoded, so the resulting string is longer than `byteLength`).
 * Used for the PKCE `code_verifier` and the `state` token. Requires the Web
 * Crypto API (`crypto.getRandomValues`); throws if it is unavailable so the
 * caller can reject before navigating.
 */
function randomUrlSafeString(byteLength: number): string {
  if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') {
    throw new Error('Web Crypto API is unavailable; cannot generate a secure PKCE verifier.');
  }
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/**
 * Compute the PKCE S256 `code_challenge` for a `code_verifier`:
 * base64url(SHA-256(verifier)). Requires `crypto.subtle`; throws if it is
 * unavailable so the caller can reject before navigating.
 */
async function base64UrlSha256(verifier: string): Promise<string> {
  if (typeof crypto === 'undefined' || !crypto.subtle || typeof crypto.subtle.digest !== 'function') {
    throw new Error('Web Crypto SubtleCrypto (crypto.subtle) is unavailable; cannot compute the PKCE challenge.');
  }
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Begin a federated login against the Cognito Hosted UI / Microsoft Entra ID.
 *
 * Generates a fresh PKCE `code_verifier` and its S256 `code_challenge`, plus a
 * random `state`, persists the verifier (and state) to sessionStorage under keys
 * the existing CallbackPage discovery loop finds, resolves the origin-correct
 * OAuth config, builds the `/oauth2/authorize` URL with `prompt` (default
 * `select_account`), and navigates the browser there.
 *
 * The function does not resolve normally on success — the page navigates away.
 * It rejects *before* any navigation if PKCE generation fails (e.g. `crypto`/
 * `crypto.subtle` unavailable) or the OAuth config cannot be resolved (e.g. an
 * unsupported origin or missing env vars), so the LoginPage can surface an error
 * and leave the user on the login screen (Requirements 1.5, 5.5, 5.6, 6.1, 6.2).
 *
 * See design.md (Function 2: beginFederatedLogin) and Requirements 1.1, 1.4,
 * 1.5, 2.1, 2.2, 4.1, 5.1, 5.5, 5.6, 6.1, 6.2.
 *
 * @param prompt The OAuth `prompt` value; defaults to `'select_account'` to
 *   always force the Entra account picker.
 */
export async function beginFederatedLogin(prompt: OAuthPrompt = 'select_account'): Promise<void> {
  // Resolve config first so an unsupported origin / missing env rejects before
  // we touch storage or navigate (Requirements 1.5, 5.5, 5.6).
  const config = getOAuthConfigForOrigin(window.location.origin);

  // Generate PKCE material. randomUrlSafeString / base64UrlSha256 throw when the
  // Web Crypto API is unavailable, rejecting before any navigation (Req 6.1).
  const verifier = randomUrlSafeString(64);
  const challenge = await base64UrlSha256(verifier);
  const state = randomUrlSafeString(32);

  // Persist the verifier where CallbackPage's discovery loop looks (it scans
  // sessionStorage first for keys containing codeVerifier/pkce). (Req 2.2)
  sessionStorage.setItem(PKCE_VERIFIER_STORAGE_KEY, verifier);
  sessionStorage.setItem(OAUTH_STATE_STORAGE_KEY, state);

  const url = buildAuthorizeUrl({
    config,
    prompt,
    codeChallenge: challenge,
    codeChallengeMethod: 'S256',
    state,
  });

  window.location.assign(url);
}

/**
 * Perform a complete federated sign-out.
 *
 * Clears the manual OAuth tokens from localStorage first (via the existing
 * `clearLocalAuthTokens` helper), then redirects the browser to the Cognito
 * Hosted UI `/logout` endpoint with `client_id` and an origin-correct,
 * registered `logout_uri`, which propagates the logout to Microsoft Entra ID.
 *
 * Ordering matters: tokens are removed *before* any redirect, so even if the
 * redirect never happens the browser is left with no stale tokens (Req 3.3).
 *
 * Idempotent / best-effort: safe to call when no tokens exist —
 * `clearLocalAuthTokens` no-ops in that case and the function still redirects to
 * `/logout` without error (Req 3.5).
 *
 * If a valid `/logout` URL cannot be constructed (unsupported origin or missing
 * `VITE_COGNITO_*` config), the tokens have still been cleared and the function
 * re-throws the error *instead of* navigating to an invalid `/logout` URL, so
 * the caller (AppLayout) can surface an error indication to the user
 * (Requirements 3.6, 5.7).
 *
 * Note: clearing Redux auth state (`clearUser`) is handled by the UI wiring
 * (AppLayout dispatches it before calling this helper); this helper owns token
 * clearing and the `/logout` redirect only.
 *
 * On success the function does not resolve normally — the page navigates away.
 *
 * See design.md (Function 4: completeFederatedSignOut) and Requirements 3.1,
 * 3.3, 3.5, 3.6, 5.2, 5.4, 5.7, 6.4.
 */
export async function completeFederatedSignOut(): Promise<void> {
  // Remove the manual OAuth tokens first, before any redirect, so the browser
  // is never left with stale tokens if the redirect does not occur (Req 3.1, 3.3).
  clearLocalAuthTokens();

  // Resolve the origin-correct OAuth config. If this throws (unsupported origin
  // or missing env vars) the tokens have already been cleared above; re-throw so
  // the caller can surface an error rather than redirecting to an invalid
  // /logout URL that Cognito would reject (Requirements 3.6, 5.7).
  const config = getOAuthConfigForOrigin(window.location.origin);

  // logout_uri is the current origin (config.redirectSignOut), which is a
  // registered logout URL on the Cognito app client (Req 5.2, 5.4). Both values
  // are URL-encoded.
  const logoutUrl =
    `https://${config.domain}/logout` +
    `?client_id=${encodeURIComponent(config.clientId)}` +
    `&logout_uri=${encodeURIComponent(config.redirectSignOut)}`;

  window.location.assign(logoutUrl);
}

/**
 * Returns the current user's ID token string, or null if not authenticated.
 */
export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

/**
 * Returns the current Amplify auth session, or null if not authenticated.
 */
export async function getCurrentSession() {
  try {
    return await fetchAuthSession();
  } catch {
    return null;
  }
}
